import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField } from '@mui/material';

interface PinInputProps {
  length: number;
  onComplete: (pin: string) => void;
  onClear?: () => void;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
}

/**
 * PIN Input Component
 * Renders a series of input fields for PIN entry with auto-focus behavior
 */
export function PinInput({
  length,
  onComplete,
  onClear,
  disabled = false,
  error = false,
  autoFocus = true,
}: PinInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Clear all inputs
  const clearInputs = () => {
    setValues(Array(length).fill(''));
    inputRefs.current[0]?.focus();
    onClear?.();
  };

  // Handle input change
  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Auto-focus next input
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if PIN is complete
    if (newValues.every(v => v !== '')) {
      const pin = newValues.join('');
      onComplete(pin);
    }
  };

  // Handle key down (backspace navigation)
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (!values[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      } else if (values[index]) {
        // Clear current input if it has a value
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);
        onClear?.();
      }
    } else if (e.key === 'Delete') {
      e.preventDefault();
      // Clear current input
      const newValues = [...values];
      newValues[index] = '';
      setValues(newValues);
      onClear?.();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      clearInputs();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').split('').slice(0, length);
    
    const newValues = [...values];
    digits.forEach((digit, idx) => {
      if (idx < length) {
        newValues[idx] = digit;
      }
    });
    setValues(newValues);

    // Focus last filled input or next empty
    const lastFilledIndex = Math.min(digits.length - 1, length - 1);
    inputRefs.current[lastFilledIndex]?.focus();

    // Check if PIN is complete
    if (newValues.every(v => v !== '')) {
      const pin = newValues.join('');
      onComplete(pin);
    }
  };

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  // Clear inputs when error prop changes to false
  useEffect(() => {
    if (!error) {
      clearInputs();
    }
  }, [error]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 0.75, sm: 1, md: 1.5 },
        justifyContent: 'center',
        flexWrap: 'nowrap',
        width: '100%',
        maxWidth: { xs: '100%', sm: 400 },
      }}
    >
      {Array.from({ length }).map((_, index) => (
        <TextField
          key={index}
          inputRef={el => (inputRefs.current[index] = el)}
          value={values[index]}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          disabled={disabled}
          type="tel"
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            autoComplete: 'off',
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 600,
              padding: '12px 8px',
              WebkitTextSecurity: 'disc',
            } as any,
          }}
          sx={{
            width: { xs: 44, sm: 52, md: 56 },
            minWidth: { xs: 40, sm: 48 },
            flex: { xs: '0 0 auto', sm: 'none' },
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: 1.5, sm: 2 },
              '& fieldset': {
                borderWidth: 2,
                borderColor: error ? 'error.main' : 'divider',
              },
              '&:hover fieldset': {
                borderColor: error ? 'error.main' : 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: error ? 'error.main' : 'primary.main',
              },
            },
          }}
        />
      ))}
    </Box>
  );
}
